import React, { useEffect, useState } from "react";
import { Container, Reveal, Overline, SectionHeading, useCollection } from "@/components/common";
import PageHero from "@/components/PageHero";
import { getContent, getSiteContent } from "@/lib/api";
import { YoutubeLogo, ArrowSquareOut, GraduationCap, IdentificationCard, BookBookmark, LinkedinLogo, Flask } from "@phosphor-icons/react";

const SOCIAL_ICONS = { "graduation-cap": GraduationCap, "id-card": IdentificationCard, book: BookBookmark, youtube: YoutubeLogo, linkedin: LinkedinLogo, "flask-conical": Flask };

export default function Media() {
  const { data: coverage } = useCollection(() => getContent("media_coverage"), []);
  const { data: vids } = useCollection(() => getContent("videos"), []);
  const [yt, setYt] = useState(null);
  const [social, setSocial] = useState([]);
  useEffect(() => {
    getSiteContent("youtube").then(setYt).catch(() => {});
    getSiteContent("social_links").then((v) => setSocial(v?.links || [])).catch(() => {});
  }, []);
  const channel = yt?.channel_url || "https://www.youtube.com/channel/UCjHAIW-dJXLraGA-rq6GGTA";
  const ytId = (s) => { if (!s) return ""; const m = String(s).match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{11})/); return m ? m[1] : String(s).trim(); };
  const featured = [
    ...vids.map((v) => ({ key: v.id, ytid: v.youtube_id ? ytId(v.youtube_id) : "", file: v.video_url || "", title: v.title })),
    ...(yt?.featured_video_ids || []).map((id) => ({ key: id, ytid: ytId(id), file: "", title: "" })),
  ].filter((v) => v.ytid || v.file);

  return (
    <div data-testid="media-page">
      <PageHero label="Media & Digital Presence" title="A Global Voice in Oncology"
        intro="Interviews, talks, videos and Dr. Sirohi's verified professional presence across the web."
        image="https://images.pexels.com/photos/8761541/pexels-photo-8761541.jpeg" />

      <section className="bg-white py-16">
        <Container>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <SectionHeading label="YouTube" title="From Dr. Sirohi's Channel" />
            <a href={channel} target="_blank" rel="noopener noreferrer" data-testid="youtube-channel-link" className="mb-12 flex items-center gap-2 rounded-md bg-[#0A1128] px-5 py-2.5 text-sm font-semibold text-white hover:bg-navy-700">
              <YoutubeLogo size={20} weight="fill" className="text-red-500" /> Visit YouTube Channel
            </a>
          </div>
          {featured.length > 0 ? (
            <div className="grid gap-6 md:grid-cols-3">
              {featured.map((v) => (
                <div key={v.key} data-testid="featured-video">
                  <div className="aspect-video overflow-hidden rounded-lg border border-slate-200 bg-black">
                    {v.file ? (
                      <video className="h-full w-full" src={v.file} controls preload="metadata" />
                    ) : (
                      <iframe className="h-full w-full" src={`https://www.youtube.com/embed/${v.ytid}`} title={v.title || "YouTube video"} allowFullScreen />
                    )}
                  </div>
                  {v.title && <p className="mt-2 font-serif text-lg text-navy">{v.title}</p>}
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-slate-300 bg-softgrey-100 p-10 text-center">
              <YoutubeLogo size={40} weight="fill" className="mx-auto text-red-500" />
              <p className="mt-3 text-sm text-softgrey-600">Featured videos can be added by the administrator. Visit the channel to watch the latest content.</p>
              <a href={channel} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-gold-dark">Open channel <ArrowSquareOut size={14} /></a>
            </div>
          )}
        </Container>
      </section>

      <section className="bg-softgrey-100 py-16">
        <Container>
          <SectionHeading label="Media Coverage" title="Features, interviews & talks" />
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {coverage.map((m, i) => (
              <Reveal key={m.id} delay={(i % 3) * 0.06}>
                <a href={m.url || "#"} target="_blank" rel="noopener noreferrer" className="group flex h-full flex-col rounded-lg border border-slate-200 bg-white p-6 transition-transform duration-200 hover:-translate-y-1">
                  <div className="flex items-center gap-2 text-xs"><span className="overline text-gold-dark">{m.media_type}</span>{m.date && <span className="text-softgrey-400">· {m.date}</span>}</div>
                  <h4 className="mt-2 flex-1 font-serif text-xl leading-snug text-navy">{m.title}</h4>
                  <p className="mt-1 text-sm text-softgrey-600">{m.publication}</p>
                  {m.description && <p className="mt-2 text-sm text-softgrey-600">{m.description}</p>}
                </a>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <section className="bg-navy py-16 text-white">
        <Container>
          <SectionHeading light label="Online Presence" title="Verified professional profiles" />
          <div className="grid gap-px overflow-hidden rounded-lg border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {social.map((s) => {
              const Icon = SOCIAL_ICONS[s.icon] || ArrowSquareOut;
              return (
                <a key={s.label} href={s.url} target="_blank" rel="noopener noreferrer" data-testid={`social-${s.label.toLowerCase().replace(/\s/g, "-")}`} className="group flex items-center gap-4 bg-navy p-7 transition-colors hover:bg-navy-800">
                  <Icon size={26} weight="light" className="text-gold" />
                  <div className="flex-1"><div className="font-serif text-xl text-white">{s.label}</div></div>
                  <ArrowSquareOut size={16} className="text-gold transition-transform group-hover:translate-x-0.5" />
                </a>
              );
            })}
          </div>
        </Container>
      </section>
    </div>
  );
}
